import type {
  ApiNodeDataExternalApi,
  JsonSchemaObject,
  NodeDataUpdateExternalApi,
} from '@onefootprint/request-types/dashboard';
import { outputSchemaFromType } from '@/playbook-builder/utils/output-type';
import type { ApplyEditMutation } from '../../../../../base-node';
import { type ExternalApiFormData, MOCK_RESPONSE_VALUE } from '../../../../types';

const toHeaderMap = (arr: Array<{ key: string; value: string }>) =>
  Object.fromEntries(arr.map(({ key, value }) => [key, value]));

type ApiWorkflowDataAssignment = {
  key: string;
  rule: 'assign_entire_output' | { jsonPath: { selector: string } };
  outputSchema?: JsonSchemaObject;
  isRequired?: boolean;
};

const buildAssignments = (config: ExternalApiFormData['config']): ApiWorkflowDataAssignment[] => {
  const assignments: ApiWorkflowDataAssignment[] = [];
  if (config.entireBody.enabled) {
    // The entire body is stored as-is and declared "any" ({}) — the backend keeps the raw JSON, which
    // can be any shape (object, array, or scalar).
    assignments.push({ key: config.entireBody.key, rule: 'assign_entire_output', outputSchema: {} });
  }
  for (const f of config.fieldExtractions) {
    assignments.push({
      key: f.key,
      rule: { jsonPath: { selector: `$.${f.selector}` } },
      // No type chosen → omit the schema (legacy behavior).
      ...(f.outputType !== undefined && { outputSchema: outputSchemaFromType(f.outputType) }),
      ...(f.isRequired && { isRequired: true }),
    });
  }
  return assignments;
};

export const createOnSubmitHandler = (
  node: { id: string; data: ApiNodeDataExternalApi },
  mutation: ApplyEditMutation,
  onSubmit?: (data: NodeDataUpdateExternalApi) => void, // For storybook testing
) => {
  return async (data: ExternalApiFormData) => {
    try {
      const nodeData = buildProxyNodeData(data, buildAssignments(data.config));

      await mutation.mutateAsync({
        kind: 'update_node',
        nodeData,
        nodeId: node.id,
      });

      onSubmit?.(nodeData);
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };
};

const buildProxyNodeData = (data: ExternalApiFormData, workflowDataAssignments: ApiWorkflowDataAssignment[]) => {
  const headers = toHeaderMap(data.config.headers);
  const queryParams = toHeaderMap(data.config.queryParams);
  const isMock = data.config.sandboxProxyConfig === MOCK_RESPONSE_VALUE;
  const useSandbox = data.config.useSandboxHttpProperties && !isMock;
  const sandboxHttpProperties = useSandbox
    ? {
        headers: toHeaderMap(data.config.sandboxHttpProperties.headers),
        queryParams: toHeaderMap(data.config.sandboxHttpProperties.queryParams),
        ...(data.config.sandboxHttpProperties.method !== 'get' && {
          body: data.config.sandboxHttpProperties.body ?? undefined,
        }),
        method: data.config.sandboxHttpProperties.method,
        path: data.config.sandboxHttpProperties.path,
      }
    : undefined;

  return {
    kind: 'external_api' as const,
    config: {
      headers,
      queryParams,
      ...(data.config.method !== 'get' && { body: data.config.body ?? undefined }),
      method: data.config.method,
      path: data.config.path,
      requireSuccessStatusCode: data.config.requireSuccessStatusCode,
      responseContentType: 'json',
      workflowDataAssignments,
      ...(sandboxHttpProperties && { sandboxHttpProperties }),
    },
    name: data.config.name,
    source: {
      proxy: {
        configId: data.config.proxyConfig!,
        ...(isMock ? { useMockData: data.config.useMockData } : { sandboxConfigId: data.config.sandboxProxyConfig }),
      },
    },
  };
};
